import { portalLevelPermissions } from '../../../../../submodules/web-shared/modules/Permissions/PortalLevelPermissions';
import PERMISSIONS_LIST from '../../../../../submodules/web-shared/modules/Permissions/permissions-list';
import { useUserCompanies } from '../../../../../submodules/web-shared/reducers/user-companies/hooks';
import { useUserRole } from '../../../../../submodules/web-shared/hooks/useUserRole';
import { COMPANY_SETTING_NAME } from '../../../../global/modules/companies/api/company-settings/enums/company-settings';
import { COMPANY_TYPE } from '../../../../../submodules/web-shared/types/Company.types';
import { ASSET_KIND } from '../../../modules/assets/api/enums/asset-kind';

const NON_ACTIONABLE_ASSET_KINDS = [ASSET_KIND.SHIP, ASSET_KIND.PLANE];

const useAssetManagerAccess = () => {
  const { isCompanySettingEnabled, current } = useUserCompanies();
  const { isAdminUser } = useUserRole();

  const hasPermission = (permission: PERMISSIONS_LIST) => {
    return portalLevelPermissions.isPermissionEnabled(permission, null);
  };

  // Determines if the user has access to Asset Manager application.
  const hasAMAccess = () => {
    return isCompanySettingEnabled(COMPANY_SETTING_NAME.ASSET_MANAGER_ACCESS)
      && hasPermission(PERMISSIONS_LIST.equipment_management_access);
  };

  /**
   * Determines if the user has access to event actions within Asset Manager application.
   * This includes permissions to view assets, manage events, and asset actions.
   */
  const hasAMEventManagementAccess = () => {
    return hasAMAccess() && hasPermission(PERMISSIONS_LIST.asset_manager_event_management);
  };

  /**
   * Determines if the user has toggle active state access for a specific asset based on its kind.
   * Ships and Planes are not editable.
   */
  const hasAssetToggleActiveStateAccess = ({ kind }: { kind: ASSET_KIND }) => {
    if (NON_ACTIONABLE_ASSET_KINDS.includes(kind)) return false;
    return hasManageAssetsAccess();
  };

  /**
   * Determines if the user has access to asset actions such as Lock within Asset Manager application.
   */
  const hasAMAssetActionsAccess = () => {
    return hasAMAccess() && hasPermission(PERMISSIONS_LIST.manage_asset_actions);
  };

  /**
    * Determines if the user has access to edit specific asset based on its kind.
   */
  const hasEditAssetAccess = (asset: { kind: ASSET_KIND }) => {
    if (asset.kind === ASSET_KIND.PLANE) return false;
    return hasManageAssetsAccess();
  };

  /**
   * Determines if the user has access to manage assets page.
   * This includes permissions to view assets table, create, edit, import and deactivate assets.
   */
  const hasManageAssetsAccess = () => {
    return hasAMAccess() && hasPermission(PERMISSIONS_LIST.manage_assets_access);
  };

  /**
   * Determines if the user has access to transfer assets functionality.
   * This includes permissions to transfer assets in bulk or individually.
   */
  const hasAssetsTransferAccess = () => {
    return hasAMAccess() && hasPermission(PERMISSIONS_LIST.asset_manager_assets_transfer);
  };

  /**
   * Determines if the user has access to product assets.
   * Only admin users or shipper company users have access.
   */
  const hasProductAssetKindAccess = () => {
    return hasAMAccess() && (isAdminUser || current?.type === COMPANY_TYPE.SHIPPER);
  };

  /**
   * Determines if the user has access to transfer specific asset based on its kind and active state.
   */
  const hasAssetTransferAccess = <T extends { kind: ASSET_KIND; active: boolean }>(asset: T) => {
    return hasAssetsTransferAccess() && asset.active && !NON_ACTIONABLE_ASSET_KINDS.includes(asset.kind);
  };

  return {
    hasAMAccess,
    hasAMEventManagementAccess,
    hasAMAssetActionsAccess,
    hasManageAssetsAccess,
    hasAssetTransferAccess,
    hasAssetsTransferAccess,
    hasAssetToggleActiveStateAccess,
    hasEditAssetAccess,
    hasProductAssetKindAccess
  };
};

export default useAssetManagerAccess;